import { enterAction } from '../lib/composer_enter.js'
import { applyComposerDisabled, composerDisabled } from '../lib/composer_disabled.js'
import { composerDraftKey, isUnsentChat } from '../lib/selected_tab_draft.js'

/**
 * ContentEditableInput hook for rich mention rendering with plain text input.
 *
 * Features:
 * - Contenteditable div for custom rendering
 * - Strips formatting on paste (plain text only)
 * - Auto-expands height
 * - Syncs content to hidden input for form submission
 * - Converts mention spans to markdown before submission
 */
export default {
  mounted () {
    this.editor = this.el
    this.hiddenInput = document.getElementById('message-input-value')
    this.placeholder = this.editor.dataset.placeholder
    this.form = this.editor.closest('form') // Find parent form

    if (!this.hiddenInput) {
      console.error('[ContentEditableInput] Hidden input not found')
      return
    }

    if (!this.form) {
      console.error('[ContentEditableInput] Form not found')
      return
    }

    // Bind updatePlaceholder so we can use it with MutationObserver
    this.boundUpdatePlaceholder = this.updatePlaceholder.bind(this)

    // A draft the user was typing on another surface. The chat is moved
    // between the modal, the side panel and a popout by reloading it in a
    // different document, which loses the composer — so unsent text is kept
    // in localStorage (same origin, so the modal and side-panel frames share
    // it) keyed by the chat, and restored here on mount. Cleared on send.
    this.restoreDraft()

    // Handle paste - strip all formatting
    this.editor.addEventListener('paste', (e) => {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
      this.boundUpdatePlaceholder()
    })

    // Handle input to update hidden field and adjust height
    this.editor.addEventListener('input', () => {
      this.syncToHiddenInput()
      this.boundUpdatePlaceholder()
      this.scheduleDraftSave()
    })

    // Handle blur to update placeholder state
    this.editor.addEventListener('blur', () => {
      this.boundUpdatePlaceholder()
    })

    // Handle focus
    this.editor.addEventListener('focus', () => {
      this.boundUpdatePlaceholder()
    })

    // Watch for any DOM changes to contenteditable
    this.observer = new MutationObserver(() => {
      this.boundUpdatePlaceholder()
    })

    this.observer.observe(this.editor, {
      childList: true,
      characterData: true,
      subtree: true
    })

    // Closed while the panel can't send (signed out): the server sets
    // `data-disabled`, kept current here.
    applyComposerDisabled(this.editor)
    this.disabledObserver = new MutationObserver(() => applyComposerDisabled(this.editor))
    this.disabledObserver.observe(this.editor, { attributes: true, attributeFilter: ['data-disabled'] })

    // Handle form submission - convert mentions to markdown
    // Use capture phase to ensure we sync BEFORE LiveView reads the form
    this.submitHandler = this.handleSubmit.bind(this)

    if (this.form) {
      this.form.addEventListener('submit', this.submitHandler, true) // Capture phase
    }

    // Handle keyboard shortcuts
    this.editor.addEventListener('keydown', (e) => {
      // Handle Enter key
      if (e.key === 'Enter') {
        // Same rule as Wavebox Connect: Enter sends; Shift/Alt+Enter and an
        // open ``` fence add a line; Ctrl/Cmd+Enter always sends; an IME that
        // is composing keeps Enter for its conversion.
        const action = enterAction({
          isComposing: e.isComposing || e.keyCode === 229,
          shiftKey: e.shiftKey,
          altKey: e.altKey,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey
        }, this.textBeforeCaret())
        if (action === 'insertNewline') {
          // Alt+Enter adds nothing by default in a contenteditable
          e.preventDefault()
          document.execCommand('insertLineBreak')
          return
        } else if (action !== 'send') {
          // Allow default behavior (browser will insert <br> or <div>)
          return
        } else {
          // Plain Enter: submit form
          e.preventDefault()
          e.stopPropagation()
          if (composerDisabled(this.editor)) { return }
          this.syncToHiddenInput()

          // Find and click the submit button
          const submitButton = this.form.querySelector('button[type="submit"]')

          if (submitButton) {
            submitButton.click()

          } else {
            // Fallback: dispatch submit event
            this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))

          }
          return
        }
      }

      // Prevent bold, italic, etc.
      if (e.metaKey || e.ctrlKey) {
        if (['b', 'i', 'u'].includes(e.key.toLowerCase())) {
          e.preventDefault()
        }
      }
    })

    // Initialize placeholder
    this.boundUpdatePlaceholder()

    // Focus after a short delay (for sandboxed iframes)
    setTimeout(() => {
      this.editor.focus()
    }, 100)

    // Listen for focus event from server
    this.handleEvent('focus_input', () => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        this.editor.focus()
      })
    })

    // Listen for clear event from server (e.g., when creating new chat)
    this.handleEvent('clear_input', () => {
      this.clear()
    })

    // A Focus "Summarize with Brainbox" request the server couldn't send
    // (quota, a missing key), back for the person to send once it's sorted.
    // Only fills the editor: never submits
    this.handleEvent('set_input', ({ text }) => {
      this.setText(text)
    })
  },

  /**
   * Fills an empty editor with plain text, syncs the hidden input,
   * placeholder and draft, and focuses the editor with the caret at the end.
   * Text the person already has (typed or a restored draft) is left alone.
   * @param {string} text - the composer text; blank or non-string is ignored
   */
  setText (text) {
    if (typeof text !== 'string' || !text.trim()) { return }
    if (this.editor.textContent.trim()) { return }
    this.editor.textContent = text
    this.syncToHiddenInput()
    this.updatePlaceholder()
    this.saveDraft()
    requestAnimationFrame(() => {
      this.editor.focus()
      const selection = window.getSelection()
      if (selection) {
        const range = document.createRange()
        range.selectNodeContents(this.editor)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    })
  },

  /**
   * Syncs contenteditable content to hidden input, converting mentions to markdown.
   */
  syncToHiddenInput () {
    const content = this.extractMarkdownContent()
    this.hiddenInput.value = content
  },

  /**
   * The editor's text from the start up to the caret, with line breaks, so the
   * Enter rule can tell whether the caret sits inside an open code fence.
   * Falls back to the whole text when the selection is not in the editor.
   */
  textBeforeCaret () {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !this.editor.contains(selection.focusNode)) {
      return this.extractMarkdownContent()
    }
    const range = document.createRange()
    range.setStart(this.editor, 0)
    range.setEnd(selection.focusNode, selection.focusOffset)
    const container = document.createElement('div')
    container.appendChild(range.cloneContents())
    return this.extractMarkdownContent(container)
  },

  /**
   * Extracts content from contenteditable and converts mentions to markdown.
   * @param {Element} [root] - the element to read; defaults to the editor
   */
  extractMarkdownContent (root = this.editor) {
    let markdown = ''

    // Walk through all child nodes
    for (const node of root.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        markdown += node.textContent
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList.contains('mention')) {
          // Mention spans are tracked as separate attachments, so skip them
          // in the text content to avoid showing raw @[Title](tab:id) in messages
        } else if (node.tagName === 'BR') {
          markdown += '\n'
        } else if (node.tagName === 'DIV') {
          // Handle divs created by contenteditable for newlines
          markdown += '\n' + this.extractTextSkippingMentions(node)
        } else {
          // Fallback to text content (skip any nested mentions)
          markdown += this.extractTextSkippingMentions(node)
        }
      }
    }

    // Contenteditable uses non-breaking spaces to keep trailing spaces at
    // the caret (including the @settings autocomplete prefix).
    return markdown.replace(/\u00a0/g, ' ')
  },

  /**
   * Extracts text content from a node, skipping mention spans.
   */
  extractTextSkippingMentions (node) {
    let text = ''
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (child.classList && child.classList.contains('mention')) {
          // Skip mention spans
        } else {
          text += this.extractTextSkippingMentions(child)
        }
      }
    }
    return text
  },

  /**
   * Shows/hides placeholder based on content.
   */
  updatePlaceholder () {
    const text = this.editor.textContent.trim()
    const hasOnlyBr = this.editor.innerHTML === '<br>' || this.editor.innerHTML === ''
    const isEmpty = text === '' || hasOnlyBr

    if (isEmpty) {
      this.editor.setAttribute('data-empty', 'true')
    } else {
      this.editor.removeAttribute('data-empty')
    }
  },

  /** Capture and discard only the submitted draft before its route can change. */
  handleSubmit () {
    // A closed composer keeps its text: the server says why it wasn't sent.
    if (composerDisabled(this.editor)) { return }
    this.syncToHiddenInput()
    const submittedDraftKey = this.draftKey()
    const submittedHTML = this.editor.innerHTML
    const wasUnsentChat = isUnsentChat(window.location)
    // Cancel the pending debounce before it can save the submitted text under
    // the new route. Other chats' drafts are never part of this cleanup.
    this.discardDraft(submittedDraftKey)
    setTimeout(() => {
      if (this.editor.innerHTML !== submittedHTML) { return }
      if (this.draftKey() !== submittedDraftKey) {
        if (!wasUnsentChat || this.hasCurrentDraft()) { return }
      }
      // LiveView has captured the form. A first send may already have patched
      // its route; clear only the unchanged submitted editor, not storage there.
      this.editor.innerHTML = ''
      this.syncToHiddenInput()
      this.updatePlaceholder()
    }, 50)
  },

  /** Whether the destination route already owns a draft that must be kept. */
  hasCurrentDraft () {
    try { return localStorage.getItem(this.draftKey()) !== null } catch { return false }
  },

  /**
   * Clears the editor content, and the saved draft with it.
   */
  clear () {
    this.editor.innerHTML = ''
    this.syncToHiddenInput()
    this.updatePlaceholder()
    this.discardDraft()
  },

  /**
   * The localStorage key for this chat's draft. Keyed by the route and the
   * chat id from the URL, so `/assistant/<id>` and `/chat/<id>` open the same
   * conversation's draft, and a fresh assistant chat ("new") does not pick up
   * a fresh plain-Brainbox draft.
   */
  draftKey () {
    return composerDraftKey(window.location)
  },

  scheduleDraftSave () {
    clearTimeout(this.draftTimer)
    this.draftTimer = setTimeout(() => this.saveDraft(), 250)
  },

  saveDraft () {
    try {
      const text = this.editor.innerText.trim()
      const key = this.draftKey()
      if (text) {
        localStorage.setItem(key, text)
      } else {
        localStorage.removeItem(key)
      }
    } catch {
      // Storage unavailable (private mode, quota) — a lost draft, not a broken composer
    }
  },

  restoreDraft () {
    try {
      const key = this.draftKey()
      const text = localStorage.getItem(key)
      if (!text || this.editor.innerText.trim()) { return }
      // Text only: mentions come back as plain "@title" text rather than pills
      this.editor.textContent = text
      this.syncToHiddenInput()
      this.updatePlaceholder()
    } catch {
      // As above
    }
  },

  discardDraft (draftKey = this.draftKey()) {
    clearTimeout(this.draftTimer)
    try {
      localStorage.removeItem(draftKey)
    } catch {
      // As above
    }
  },

  destroyed () {
    clearTimeout(this.draftTimer)
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.disabledObserver) {
      this.disabledObserver.disconnect()
    }
    if (this.form && this.submitHandler) {
      this.form.removeEventListener('submit', this.submitHandler, true)
    }
  }
}
